import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";
import { track } from "../lib/track";
import "./Onboarding.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: "welcome", label: "Welcome" },
  { key: "profile", label: "Parish Profile" },
  { key: "generate", label: "Intelligence" },
  { key: "success", label: "Your Plan" },
];

function pollJob(jobId) {
  return new Promise((resolve, reject) => {
    let n = 0;
    const tick = async () => {
      n += 1;
      try {
        const r = await axios.get(`${API}/parish/job/${jobId}`);
        if (r.data.status === "done") return resolve(r.data.report);
        if (r.data.status === "error") return reject(new Error(r.data.detail || "Generation failed."));
        if (n > 60) return reject(new Error("Taking longer than expected. Please try again."));
        setTimeout(tick, 3000);
      } catch (e) {
        if (n > 60) return reject(e);
        setTimeout(tick, 3000);
      }
    };
    setTimeout(tick, 3000);
  });
}

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [step, setStep] = useState("welcome");
  const [hasName, setHasName] = useState(false);
  const [phase, setPhase] = useState("");        // generating sub-phase copy
  const [plan, setPlan] = useState(null);        // resulting SundayBuilder plan
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const startedRef = useRef(false);

  const refreshProfile = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/parish-profile`);
      const name = r.data?.profile?.identity?.parish_name;
      setHasName(Boolean(name));
      return Boolean(name);
    } catch { return false; }
  }, []);

  // Entry: if onboarding already done, go to dashboard. Otherwise begin the journey.
  useEffect(() => {
    if (user === null) return;            // still loading
    if (user === false) { navigate("/login", { replace: true }); return; }
    const ob = user.entitlements?.onboarding || {};
    if (ob.sundaybuilder_used) { navigate("/dashboard", { replace: true }); return; }
    if (!startedRef.current) {
      startedRef.current = true;
      track("Guided Welcome Started");
    }
    refreshProfile().then((ok) => { if (ok) setStep("profile"); });
  }, [user, navigate, refreshProfile]);

  const beginSetup = async () => {
    track("Guided Welcome Completed");
    track("Parish Profile Started");
    const ok = await refreshProfile();
    setStep(ok ? "generate" : "profile");
    if (ok) runGeneration();
  };

  const continueFromProfile = async () => {
    const ok = await refreshProfile();
    if (!ok) { setError("Please add at least your Parish Name in the Parish Profile, then continue."); return; }
    setError("");
    track("Parish Profile Completed");
    setStep("generate");
    runGeneration();
  };

  const runGeneration = useCallback(async () => {
    setError("");
    try {
      // 1) Complimentary Parish Intelligence Report (initial assessment)
      setPhase("Reading your parish's identity, tradition, and ministry context…");
      const piStart = await axios.post(`${API}/parish-profile/insights`);
      const piReport = await pollJob(piStart.data.job_id);
      // Save the assessment to the user's history
      try {
        const r = await axios.get(`${API}/parish-profile`);
        const id = r.data?.profile?.identity || {};
        const intake = { ...id, city_state: [id.city, id.state].filter(Boolean).join(", ") };
        await axios.post(`${API}/parish/save`, { intake, report: piReport });
      } catch { /* ignore */ }

      // 2) Complimentary SundayBuilder™ worship plan, grounded in that profile
      setPhase("Building your first worship plan from your parish's living context…");
      const sbStart = await axios.post(`${API}/sundaybuilder/generate`, { scope: "Single Sunday" });
      const sbPlan = await pollJob(sbStart.data.job_id);
      setPlan(sbPlan);
      await checkAuth();             // refresh entitlements (onboarding flags now used)
      track("Complimentary Report Viewed");
      setStep("success");
    } catch (e) {
      setError(e?.response?.data?.detail?.message || e?.message || "Something went wrong. Please try again.");
    }
  }, [checkAuth]);

  const startCheckout = async (planKey) => {
    track("Upgrade Button Clicked", { plan: planKey, from: "onboarding" });
    setBusy(planKey);
    try {
      const { data } = await axios.post(`${API}/billing/checkout`, { plan: planKey, origin: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setError(e.response?.data?.detail || "Could not start checkout. Please try again.");
      setBusy("");
    }
  };

  const activeIdx = STEPS.findIndex((s) => s.key === step);

  return (
    <div className="onb" data-testid="onboarding-page">
      <div className="onb-top">
        <div className="onb-brand">
          <img src="/brand/li-logo-sm.png" alt="Liturgical Intelligence™" />
        </div>
        <button className="onb-skip" onClick={() => navigate("/dashboard")} data-testid="onb-skip">Skip to dashboard →</button>
      </div>

      <div className="onb-rail" data-testid="onb-rail">
        {STEPS.map((s, i) => (
          <div key={s.key} className={`onb-rail-step${i <= activeIdx ? " on" : ""}${i === activeIdx ? " current" : ""}`}>
            <span className="onb-rail-dot">{i + 1}</span><span className="onb-rail-label">{s.label}</span>
          </div>
        ))}
      </div>

      <div className="onb-body">
        {error && <div className="onb-error" data-testid="onb-error">{error}</div>}

        {step === "welcome" && (
          <div className="onb-card onb-welcome" data-testid="onb-welcome">
            <div className="onb-eyebrow">Welcome to Liturgical Intelligence™</div>
            <h1>Let's build your parish's worship intelligence.</h1>
            <p className="onb-lead">
              In a few minutes you'll experience the complete Liturgical Intelligence™ workflow —
              your living <b>Parish Profile</b>, a strategic <b>Parish Intelligence Report</b>, and your
              first complimentary <b>SundayBuilder™</b> worship plan, all grounded in your parish's own
              tradition, ministry, and music.
            </p>
            <ul className="onb-journey">
              <li><span>1</span> Tell us about your parish</li>
              <li><span>2</span> Receive your Parish Intelligence assessment</li>
              <li><span>3</span> Generate your first worship plan — on us</li>
            </ul>
            <button className="onb-btn gold" onClick={beginSetup} data-testid="onb-begin">Begin Your Setup</button>
            <p className="onb-fine">Instant access. No credit card required for your complimentary plan.</p>
          </div>
        )}

        {step === "profile" && (
          <div className="onb-card" data-testid="onb-profile">
            <div className="onb-eyebrow">Step 2 · Your Parish Profile</div>
            <h1>Tell us about your parish.</h1>
            <p className="onb-lead">
              Your Parish Profile is the living context behind every plan — identity, worship schedule,
              clergy preferences, music resources, and customs. The more you share, the more your reports
              reflect <i>your</i> parish, not a generic template.
            </p>
            <div className="onb-actions">
              <button className="onb-btn gold" onClick={() => navigate("/parish-profile")} data-testid="onb-open-profile">Complete Parish Profile</button>
              <button className="onb-btn ghost" onClick={continueFromProfile} disabled={!hasName && false} data-testid="onb-continue">
                I've finished — continue
              </button>
            </div>
            <p className="onb-fine">{hasName ? "Profile detected ✓ — you're ready to continue." : "Add at least your Parish Name to continue."}</p>
          </div>
        )}

        {step === "generate" && (
          <div className="onb-card onb-generate" data-testid="onb-generating">
            <div className="onb-spin" />
            <div className="onb-eyebrow">Ordo AI™ at work</div>
            <h1>Creating your complimentary experience…</h1>
            <p className="onb-lead" data-testid="onb-phase">{phase || "Preparing…"}</p>
            <p className="onb-fine">This usually takes 2–4 minutes. Please keep this page open.</p>
          </div>
        )}

        {step === "success" && (
          <div className="onb-success" data-testid="onb-success">
            <div className="onb-card onb-success-head">
              <div className="onb-badge">Complimentary Plan Ready</div>
              <h1>Your Complimentary SundayBuilder™ Plan is Ready</h1>
              <p className="onb-lead">
                You have successfully generated your complimentary worship plan. We hope this has
                demonstrated the depth and quality of Liturgical Intelligence™.
              </p>
              {plan && (
                <div className="onb-plan-preview" data-testid="onb-plan-preview">
                  <div className="onb-plan-title">{plan.title || "Worship Plan"}</div>
                  {plan.overview && <p>{plan.overview}</p>}
                  <button className="onb-btn ghost" onClick={() => navigate("/worship-planner")} data-testid="onb-view-plan">View full plan in SundayBuilder™</button>
                </div>
              )}
              <p className="onb-note">
                This plan was created using your parish's unique worship traditions, ministry profile,
                and planning preferences — and your Parish Intelligence Report has been saved to your account.
              </p>
            </div>

            <div className="onb-eyebrow center">Continue planning worship for your parish</div>
            <div className="onb-offers">
              <div className="onb-offer" data-testid="offer-single">
                <div className="onb-offer-tag">One-Time</div>
                <h3>Single Report</h3>
                <div className="onb-price">$29</div>
                <p>Perfect for special liturgies, guest planning, or occasional worship needs.</p>
                <ul>
                  <li>One complete SundayBuilder™ package</li>
                  <li>PDF export & professional formatting</li>
                  <li>One-time purchase</li>
                </ul>
                <button className="onb-btn" disabled={busy} onClick={() => startCheckout("single_report")} data-testid="buy-single">
                  {busy === "single_report" ? "Redirecting…" : "Purchase Single Report"}
                </button>
              </div>

              <div className="onb-offer featured" data-testid="offer-pro">
                <div className="onb-offer-ribbon">Most Popular</div>
                <h3>Parish Pro</h3>
                <div className="onb-price">$49<span>/month</span></div>
                <p>Your parish's permanent worship planning system. The intelligence learns your parish over time — every season becomes easier, every year builds on the last.</p>
                <ul>
                  <li>Unlimited SundayBuilder™ planning</li>
                  <li>Parish Profile, Planning Scope & Report History</li>
                  <li>Seasonal, Holy Week, Christmas & Annual planning</li>
                  <li>AI Parish Memory & all future modules</li>
                  <li>Priority support</li>
                </ul>
                <button className="onb-btn gold" disabled={busy} onClick={() => startCheckout("parish_pro")} data-testid="buy-pro">
                  {busy === "parish_pro" ? "Redirecting…" : "Upgrade to Parish Pro"}
                </button>
              </div>
            </div>
            <button className="onb-laterlink" onClick={() => navigate("/dashboard")} data-testid="onb-maybe-later">Maybe later — go to my dashboard</button>
          </div>
        )}
      </div>
    </div>
  );
}
